// @flow
import * as React from 'react';
import {useState} from 'react';
import PropTypes from 'prop-types';

import type {PaymentMethod, Currency} from '../../../../../entities';
import {Link} from 'react-router-dom';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  makeStyles,
  Typography,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import {useEffect} from 'react';
import {ArrowBack} from '@material-ui/icons';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import type {PaymentMethodAction, IziPayRequest} from '../../duck';
import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import NewFeatureBadge from '../../../../../elements/NewFeatureBadge/NewFeatureBadge';
import {IZIPAY_SUPPORTED_CURRENCY_CODES} from '../../utils/paymentData';

type Props = {|
  isSaving?: boolean,
  updateIziPay: (payload: PaymentMethodAction) => void,
  paymentMethod: PaymentMethod,
  currency?: Currency,
  disableActivation?: boolean,
|};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
    },
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  checkbox: {
    marginTop: theme.spacing(2),
  },
  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  descriptionText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  row: {
    display: 'flex',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      '&:not(:last-child)': {
        marginRight: theme.spacing(2),
      },
    },
  },
  saveButton: {
    margin: theme.spacing(3, 0),
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
}));

export default function IziPayConfig(props: Props) {
  const {
    isSaving,
    updateIziPay,
    paymentMethod,
    currency,
    disableActivation,
  } = props;

  const classes = useStyles();

  const [disabledButton, setDisabledButton] = useState(false);
  const [showWarningCurrency, setShowWarningCurrency] = useState(false);
  const [enabled, setEnabled] = useState(
    paymentMethod ? paymentMethod.enabled : false
  );

  useEffect(() => {
    if (paymentMethod && paymentMethod.enabled)
      setEnabled(paymentMethod.enabled);
  }, [paymentMethod]);

  const {register, handleSubmit, errors, formState} = useForm({
    mode: 'onChange',
  });
  const {isDirty} = formState;

  const handleToggleEnabled = event => {
    if (event) {
      setEnabled(event.target.checked);
      if (!event.target.checked) {
        const iziPayRequest = {
          enabled: false,
        };
        updateIziPay({
          iziPayRequest,
        });
      }
    }
  };

  const handleFormSubmit = data => {
    const iziPayRequest: IziPayRequest = {
      enabled: true,
      serverParameters: {
        publicKey: data.publicKey.trim(),
        secretKey: data.secretKey.trim(),
        vadsSiteId: data.vadsSiteId.trim(),
      },
      displayName:
        paymentMethod && paymentMethod.displayName === data.displayName
          ? undefined
          : data.displayName,
      clientParameters:
        paymentMethod &&
        paymentMethod.clientParameters &&
        paymentMethod.clientParameters.description === data.description
          ? undefined
          : {description: data.description || undefined},
    };
    updateIziPay({
      iziPayRequest,
    });
  };

  useEffect(() => {
    if (!IZIPAY_SUPPORTED_CURRENCY_CODES.includes(currency?.code)) {
      setShowWarningCurrency(true);
      setDisabledButton(true);
    }
  }, [currency]);

  return (
    <div className={classes.root}>
      <Snackbar
        open={showWarningCurrency}
        autoHideDuration={8000}
        onClose={() => setShowWarningCurrency(false)}
      >
        <Alert severity="warning" onClose={() => setShowWarningCurrency(false)}>
          La moneda configurada en tu tienda no es compatible con IziPay.
        </Alert>
      </Snackbar>
      <CustomExitPrompt when={isDirty} />
      <div className={classes.subRouteHeader}>
        <IconButton
          variant="contained"
          size="small"
          component={Link}
          to={`/configure/payment-methods${window.location.search}`}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          className={classes.subRouteTitle}
          id="mercadopago-configuration"
        >
          Izi Pay <NewFeatureBadge />
        </Typography>
      </div>
      <Divider className={classes.divider} />
      {typeof disableActivation === 'boolean' && (
        <FormControlLabel
          classes={{label: classes.checkboxLabel}}
          className={classes.checkbox}
          control={
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              color="primary"
              disabled={isSaving || disableActivation}
            />
          }
          label={
            disableActivation
              ? 'Debes configurar tus propias tarifas de envío para habilitar esta opción'
              : 'Activar'
          }
        />
      )}
      {enabled && (
        <>
          <Typography className={classes.descriptionText}>
            Debes colocar aquí las llaves de iziPay. Si no tienes una cuenta,
            seguir los{' '}
            <a
              href="https://tiendadateam.notion.site/C-mo-configurar-pagos-con-tarjeta-de-cr-dito-Izipay-Per-caad7ec504d14d3b8868e9500d174d36"
              target="_blank"
              rel="noopener noreferrer"
            >
              siguientes pasos
            </a>
          </Typography>

          <CustomTextField
            className={classes.textField}
            label="Identificador de la tienda/usuario"
            name="vadsSiteId"
            defaultValue={
              paymentMethod && paymentMethod.serverParameters
                ? paymentMethod.serverParameters.vads_site_id
                : ''
            }
            error={!!errors.vadsSiteId}
            helperText={errors.vadsSiteId ? errors.vadsSiteId.message : ''}
            inputRef={register({
              required: 'Campo obligatorio',
            })}
            type="text"
            disabled={isSaving}
          />
          <div className={classes.row}>
            <CustomTextField
              className={classes.textField}
              label="Contraseña"
              name="secretKey"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.secret_key
                  : ''
              }
              error={!!errors.secretKey}
              helperText={errors.secretKey ? errors.secretKey.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={isSaving}
            />
            <CustomTextField
              className={classes.textField}
              label="LLave pública"
              name="publicKey"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.public_key
                  : ''
              }
              error={!!errors.publicKey}
              helperText={errors.publicKey ? errors.publicKey.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={isSaving}
            />
          </div>
          <CustomTextField
            className={classes.textField}
            label="Nombre del método de pago que verá tu cliente"
            name="displayName"
            defaultValue={
              paymentMethod
                ? paymentMethod.displayName
                : 'Tarjeta de crédito/débito'
            }
            error={!!errors.displayName}
            helperText={errors.displayName ? errors.displayName.message : ''}
            inputRef={register({
              required: 'Campo obligatorio',
            })}
            type="text"
            disabled={isSaving}
          />
          <CustomTextField
            className={classes.textField}
            multiline
            label="Instrucciones (Opcional)"
            name="description"
            error={!!errors.description}
            helperText={errors.description ? errors.description : ''}
            defaultValue={
              paymentMethod && paymentMethod.clientParameters
                ? paymentMethod.clientParameters.description
                : ''
            }
            type="text"
            inputRef={register}
            rows={4}
            disabled={isSaving}
          />
          <CustomButton
            variant="contained"
            color="primary"
            disabled={disabledButton}
            className={classes.saveButton}
            onClick={handleSubmit(handleFormSubmit)}
          >
            Guardar
          </CustomButton>
        </>
      )}
    </div>
  );
}

IziPayConfig.propTypes = {
  isSaving: PropTypes.bool,
  paymentMethod: PropTypes.object,
  updateIziPay: PropTypes.func.isRequired,
  currency: PropTypes.object,
  disableActivation: PropTypes.bool,
};
